import { useMemo } from 'react';
import { PerformanceTrafficLight, SIGNAL_META } from './PerformanceTrafficLight';
import { Sparkles } from 'lucide-react';

/**
 * Normalize Portuguese text: lowercase, remove diacritics, collapse whitespace.
 */
const normalize = (s) =>
  (s || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

/**
 * Detect a concept in the text using fuzzy-ish matching:
 *  - normalize both sides
 *  - check if all words of the concept (>= 3 chars) appear in the normalized text
 *  - also accept stemming-light: concept word found if its 4-char prefix appears
 */
const conceptIsCovered = (concept, normalizedText) => {
  const conceptNorm = normalize(concept);
  if (!conceptNorm) return false;
  const tokens = conceptNorm.split(' ').filter(t => t.length >= 3);
  if (tokens.length === 0) return false;
  return tokens.every((tok) => {
    if (normalizedText.includes(tok)) return true;
    // prefix tolerance (e.g. "fotossintese" matches "fotossint")
    const prefix = tok.slice(0, Math.max(4, Math.floor(tok.length * 0.7)));
    return prefix.length >= 4 && normalizedText.includes(prefix);
  });
};

/**
 * Compute the live signal (green/yellow/red/pending) for an essay answer.
 * Rules (per teacher choice):
 *   Green  >= 70% conceitos + texto >= 50 palavras
 *   Yellow >= 40% conceitos + texto >= 25 palavras
 *   Red    otherwise (or texto curto)
 */
export const computeLiveSignal = (text, keyConcepts) => {
  const concepts = Array.isArray(keyConcepts) ? keyConcepts.filter(c => (c || '').trim().length > 0) : [];
  const trimmed = (text || '').trim();
  const wordCount = trimmed ? trimmed.split(/\s+/).filter(Boolean).length : 0;

  if (wordCount === 0) {
    return { signal: 'pending', coverage: 0, wordCount: 0, matchedCount: 0, total: concepts.length };
  }

  const normalized = normalize(trimmed);
  let matched = 0;
  for (const c of concepts) {
    if (conceptIsCovered(c, normalized)) matched += 1;
  }
  const total = concepts.length || 1;
  const coverage = matched / total;

  let signal;
  if (coverage >= 0.7 && wordCount >= 50) signal = 'green';
  else if (coverage >= 0.4 && wordCount >= 25) signal = 'yellow';
  else signal = 'red';

  return { signal, coverage, wordCount, matchedCount: matched, total: concepts.length };
};

/**
 * LiveEssayCoach — shown next to the textarea while the student writes.
 * Props:
 *  - text: current essay text
 *  - keyConcepts: array of strings
 *  - testIdPrefix: prefix for data-testids
 */
export function LiveEssayCoach({ text, keyConcepts, testIdPrefix = 'essay-coach' }) {
  const analysis = useMemo(() => computeLiveSignal(text, keyConcepts), [text, keyConcepts]);
  const meta = SIGNAL_META[analysis.signal] || SIGNAL_META.pending;

  if (!keyConcepts || keyConcepts.length === 0) return null;

  const progressNeeded =
    analysis.signal === 'green'
      ? null
      : analysis.signal === 'yellow'
        ? `Para chegar ao Verde: cobrir ${Math.max(0, Math.ceil(analysis.total * 0.7) - analysis.matchedCount)} conceito(s) e ${Math.max(0, 50 - analysis.wordCount)} palavra(s) a mais.`
        : `Para chegar ao Amarelo: cobrir ${Math.max(0, Math.ceil(analysis.total * 0.4) - analysis.matchedCount)} conceito(s) e ${Math.max(0, 25 - analysis.wordCount)} palavra(s) a mais.`;

  return (
    <div
      data-testid={`${testIdPrefix}-card`}
      className={`relative rounded-xl border-2 ${meta.border} ${meta.soft} p-3 transition-colors`}
    >
      <div className="flex items-start gap-3">
        <PerformanceTrafficLight signal={analysis.signal} size="md" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[10px] font-extrabold uppercase tracking-widest ${meta.text}`}>
              Treinador IA
            </span>
            <span className={`text-xs font-bold ${meta.text}`} data-testid={`${testIdPrefix}-label`}>
              {meta.label}
            </span>
          </div>
          {analysis.signal !== 'pending' && (
            <div className="mt-1 text-xs text-slate-700 leading-snug">
              <span data-testid={`${testIdPrefix}-stats`}>
                {analysis.matchedCount}/{analysis.total} conceitos detectados · {analysis.wordCount} palavra{analysis.wordCount === 1 ? '' : 's'}
              </span>
            </div>
          )}
          {progressNeeded && analysis.signal !== 'pending' && (
            <div className="mt-1 text-[11px] text-slate-600 italic">
              {progressNeeded}
            </div>
          )}
          {analysis.signal === 'green' && (
            <div className="mt-1 text-[11px] text-emerald-700 font-semibold flex items-center gap-1">
              <Sparkles className="w-3 h-3" /> Sua resposta está completa! Revise antes de enviar.
            </div>
          )}
        </div>
      </div>

      {analysis.total > 0 && (
        <div className="mt-3 h-1.5 w-full bg-white/60 rounded-full overflow-hidden">
          <div
            className={`h-full bg-gradient-to-r ${meta.gradient} transition-all duration-500`}
            style={{ width: `${Math.min(100, Math.round(analysis.coverage * 100))}%` }}
          />
        </div>
      )}
    </div>
  );
}

export default LiveEssayCoach;
